import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Box, Typography } from '@mui/material';
import { WithdrawalForm } from '../WithdrawForm';
import { WithdrawFundsSummary } from '../WithdrawFundsSummary';
import { SummaryLabel } from '../../SummaryLabel';
import { WithdrawCryptoProviderForm } from '../WithdrawCryptoProviderForm';
import { WithdrawCryptoInstructions } from '../WithdrawCryptoInstructions';
import { WithdrawFundsSummaryDto } from 'app/interfaces/dto';
import { useSelector } from 'react-redux';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import { useWithdrawCryptoForm } from 'modules/payments/hooks/use-withdraw-crypto-form';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { exchangeRatesSelector } from 'modules/payments/store/exchange-rates/selectors';
import { UserQuotaStatusDto } from 'app/interfaces/dto/user-quota-status.dto';
import {
  confirmWithdrawCrypto,
  getWithdrawCryptoSummary,
} from 'modules/payments/api/withdraw-crypto';
import { useCancelToken } from 'app/services/api/hooks';
import { MIN_WITHDRAW_CRYPTO_AMOUNTS } from '@okletsplay/core';
import { UseFormReturn } from 'react-hook-form';
import { WithdrawFundsFields } from 'modules/payments/interfaces';
import { WITHDRAW_CRYPTO_AMOUNTS } from 'modules/payments/constants';

interface ComponentProps {
  isLoading: boolean;
  setLoading: (value: boolean) => void;
  onValidChange: (value: boolean) => void;
  onSuccessSubmit: () => void;
  quotas: UserQuotaStatusDto | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: any;
}

export const WithdrawCryptoContent: FC<ComponentProps> = forwardRef(
  ({ isLoading, setLoading, onValidChange, onSuccessSubmit, quotas }, ref) => {
    const cancelToken = useCancelToken();

    const [summary, setSummary] = useState<undefined | WithdrawFundsSummaryDto>();

    const singleCurrencyAccounts = useSelector(walletSelector.asAccounts);
    const userAccounts = useSelector(userPaymentAccountsSelector.all);
    const exchangeRates = useSelector(exchangeRatesSelector.all);

    const { form } = useWithdrawCryptoForm(
      () => (MultiCurrencyConfig.isWithdrawalEnabled() ? userAccounts : singleCurrencyAccounts),
      () => exchangeRates,
      () => quotas
    );

    const {
      getValues,
      formState: { isValid, isValidating },
      watch,
    } = form;

    const [currency] = watch(['currency']);

    useEffect(() => {
      if (!isValid || !isValidating) {
        setSummary(undefined);
      }
      if (!isValid || isValidating) return;

      setLoading(true);

      getWithdrawCryptoSummary(getValues(), cancelToken).then(
        (newSummary) => {
          setLoading(false);
          setSummary(newSummary);
        },
        () => {
          setLoading(false);
          setSummary(undefined);
        }
      );
    }, [isValid, isValidating, getValues, cancelToken, setLoading]);

    const handleSubmit = useCallback(() => {
      if (!isValid) return;
      if (isLoading) return;

      setLoading(true);

      confirmWithdrawCrypto(getValues(), cancelToken).then(
        () => {
          onSuccessSubmit();
        },
        () => {
          setLoading(false);
        }
      );
    }, [isValid, isLoading, setLoading, getValues, cancelToken, onSuccessSubmit]);

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    useEffect(() => {
      setSummary(undefined);
    }, [currency]);

    useEffect(() => {
      onValidChange(isValid && !isValidating);
    }, [isValid, isValidating, onValidChange]);

    return (
      <>
        <Box mb={2}>
          <Typography mb={2} variant="h6">
            Select a withdrawal amount:
          </Typography>

          <WithdrawalForm
            form={form as never as UseFormReturn<WithdrawFundsFields>}
            isDisabled={isLoading}
            minAmounts={MIN_WITHDRAW_CRYPTO_AMOUNTS}
            preselectedAmounts={WITHDRAW_CRYPTO_AMOUNTS}
          />
        </Box>

        <Box mb={1}>
          <WithdrawCryptoProviderForm form={form} isDisabled={isLoading} />
        </Box>

        <Box mb={2}>
          <Typography variant="h6" mb={2}>
            Withdraw instructions:
          </Typography>
          <WithdrawCryptoInstructions />
        </Box>

        {summary ? (
          <>
            <SummaryLabel labelText="Transaction summary" />
            <WithdrawFundsSummary
              summary={summary}
              source={{ amount: getValues().amount, currency: getValues().currency }}
              title="Crypto to Crypto Transfer"
            />
          </>
        ) : null}
      </>
    );
  }
);
